import { Box, styled } from '@mui/material';
import React from 'react';

import { MOTION_TOKENS } from 'views/playground/features/data-lab/constants/motion-tokens';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';

export type KeyboardOption = { id: string; label: string };

type Props = {
  options: KeyboardOption[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  ariaLabel: string;
  // Toggleable options can be unselected. Forward-navigation options cannot,
  // and therefore do not use `aria-pressed`.
  toggleable?: boolean;
};

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const Pill = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'chartPalette',
})<{ isSelected: boolean; chartPalette: ChartPalette }>(({ theme, isSelected, chartPalette }) => ({
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    appearance: 'none',
    cursor: 'pointer',
    padding: theme.spacing(1, 3),
    borderRadius: 999,
    border: `1px solid ${isSelected ? chartPalette.series.categorical[0] : chartPalette.borders.subtle}`,
    backgroundColor: isSelected ? chartPalette.backgrounds.raised : 'transparent',
    color: isSelected ? chartPalette.text.primary : chartPalette.text.secondary,
    transition: `border-color ${MOTION_TOKENS.fade.duration}ms ${MOTION_TOKENS.fade.easing}, background-color ${MOTION_TOKENS.fade.duration}ms ${MOTION_TOKENS.fade.easing}`,

    '&:hover': {
      borderColor: chartPalette.borders.strong,
    },

    '&:focus-visible': {
      outline: `2px solid ${chartPalette.series.categorical[0]}`,
      outlineOffset: 2,
    },
}));

// Real buttons provide keyboard selection when a chart mark is mouse-only.
const KeyboardOptionList: React.FC<Props> = ({ options, selectedId, onSelect, ariaLabel, toggleable = true }) => {
  const chartPalette = useChartPalette();

  return (
    <Root role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const isSelected = option.id === selectedId;
        return (
          <Pill
            key={option.id}
            type="button"
            isSelected={isSelected}
            chartPalette={chartPalette}
            aria-pressed={toggleable ? isSelected : undefined}
            onClick={() => onSelect(toggleable && isSelected ? null : option.id)}>
            {option.label}
          </Pill>
        );
      })}
    </Root>
  );
};

export default KeyboardOptionList;
